import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import React, { useEffect } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import character from '../../assets/images/intake/step2/char.svg';
import { useIntakeFormContext } from '../../hooks/use-form-context.hooks';
import { usePreEmploymentAssessmentContext } from '../../hooks/use-pre-employment-assessment.hooks';
import { dispatchUpdateIntakeFormAgeRange } from '../../redux/slice/form-intake/form-intake.operations';
import { IntakeFormAgeRangeDTO } from '../../redux/slice/form-intake/form-intake.types';
import { AgeRange as AgeRangeValue } from '../../redux/slice/form-intake/form-intake.types';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface FormData extends Omit<IntakeFormAgeRangeDTO, 'ageRange'> {
  ageRange: string;
}
interface AgeRangeProps {
  step: number;
  onBack: () => void;
}
const age_options = (Object.keys(AgeRangeValue) as Array<keyof typeof AgeRangeValue>)
  .filter((key) => typeof AgeRangeValue[key] === 'string')
  .map((key) => ({ label: AgeRangeValue[key], value: AgeRangeValue[key] }));

export const AgeRange: React.FC<AgeRangeProps> = ({ step, onBack }) => {
  const navigate = useNavigate();
  const { disablePersonalInfo } = usePreEmploymentAssessmentContext();
  const { ageRange } = useIntakeFormContext();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(
      yup
        .object({
          ageRange: yup.string().required('Age range is required'),
        })
        .required(),
    ),
    defaultValues: { ageRange: ageRange || '' },
  });
  const dispatch = useAppDispatch();

  const submitAgeRange: SubmitHandler<FormData> = async (data: FormData) => {
    logger.debug('Submitted age range.', data);
    await dispatch(dispatchUpdateIntakeFormAgeRange(data as IntakeFormAgeRangeDTO));
    navigate(`/pre-employment/intake/step${step + 1}`);
  };

  const backButtonClick = () => {
    onBack();
  };

  useEffect(() => {
    window.scrollTo(0, 0); // Reset scroll to the top
  }, []);

  useEffect(() => {
    reset({ ageRange });
  }, [ageRange, reset]);

  return (
    <div>
      <div className="flex mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">02</div>
        <div>
          <h4 className="text-2xl">
            Age <span className="font-semibold"> Range</span>
          </h4>
          <p className="text-lg">Help us know you better by selecting your age range:</p>
        </div>
      </div>
      <div className="character absolute bottom-0 left-[-138px]">
        <img className="" src={character} alt="" />
      </div>
      <Box
        className="md:px-10"
        component="form"
        sx={{
          '& > :not(style)': { mb: 4 },
        }}
        autoComplete="off"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitAgeRange)}
      >
        <div className="h-64">
          <Controller
            control={control}
            name="ageRange"
            render={({ field: { value, onChange } }) => (
              <TextField
                required
                className="w-full"
                id="outlined-select-age"
                select
                label="Age Range"
                onChange={(event) => onChange(event.target.value)}
                value={value}
                helperText=""
                defaultValue=""
              >
                <MenuItem key="select" value="">
                  Select
                </MenuItem>
                {age_options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          ></Controller>
          {errors.ageRange && (
            <div>
              <p className="text-red-500">{errors.ageRange?.message}</p>
            </div>
          )}
        </div>
        <div className="flex justify-between mt-8">
          {!disablePersonalInfo && (
            <button onClick={backButtonClick} className="btn-primary">
              Back
            </button>
          )}
          <button className="btn-primary">Next</button>
        </div>
      </Box>
    </div>
  );
};
